import React, { useMemo } from 'react';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMonthPickupOrders } from '../../hooks/useOperationsData';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { buildMaterialDailySplit } from '../../utils/dailyMaterials.helpers';
import DailyMaterialChart from './DailyMaterialChart';
import type { DailyChartType } from './DailyRequestsChart';

interface DailyMaterialSplitProps {
  orgId: string;
  monthStart: Date;
  /** The dashboard's fetch time — refreshing the dashboard refetches the split. */
  syncKey: number | null;
  /** Month total of the daily chart (kg), to flag when the two disagree. */
  expectedTotalKg: number;
  type: DailyChartType;
  ariaLabel: string;
  height?: number;
}

/**
 * The daily chart split by material, built from the month's pickup orders
 * (the dashboard API only sends one total per day). States what it covers:
 * the order count, a partial list, or a total that drifted from the
 * dashboard's.
 */
const DailyMaterialSplit: React.FC<DailyMaterialSplitProps> = ({
  orgId,
  monthStart,
  syncKey,
  expectedTotalKg,
  type,
  ariaLabel,
  height = 260,
}) => {
  const { t } = useTranslation();
  const fmt = useNumberFormat();
  const query = useMonthPickupOrders(orgId, monthStart, syncKey, true);
  const monthKey = monthStart.getTime();
  const split = useMemo(
    () => buildMaterialDailySplit(query.data.orders, new Date(monthKey)),
    [query.data.orders, monthKey],
  );

  if (query.status === 'loading' || query.status === 'idle') {
    return (
      <Box aria-busy="true" aria-label={t('dashboard.daily.split.loading')} sx={{ display: 'grid', gap: 1.5 }}>
        <Skeleton variant="rounded" height={height} />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="40%" />
      </Box>
    );
  }

  if (query.status === 'forbidden' || query.status === 'error') {
    return (
      <Box sx={{ minHeight: height, display: 'grid', placeItems: 'center', textAlign: 'center', px: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {query.status === 'forbidden' ? t('dashboard.daily.split.forbidden') : t('dashboard.daily.split.error')}
          </Typography>
          {query.status === 'error' ? (
            <Button size="small" onClick={query.refetch} sx={{ mt: 1 }}>
              {t('dashboard.daily.split.retry')}
            </Button>
          ) : null}
        </Box>
      </Box>
    );
  }

  if (split.orderCount === 0) {
    return (
      <Box sx={{ minHeight: height, display: 'grid', placeItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('dashboard.daily.split.empty')}
        </Typography>
      </Box>
    );
  }

  const gap = Math.abs(split.totalKg - expectedTotalKg);
  const drifted = !query.data.truncated && gap > Math.max(1, expectedTotalKg * 0.001);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 1.5, opacity: query.isFetching ? 0.6 : 1, transition: 'opacity 160ms' }}>
      <DailyMaterialChart split={split} type={type} height={height} ariaLabel={ariaLabel} />
      <Typography variant="caption" component="p" color="text.secondary" sx={{ m: 0 }}>
        {t('dashboard.daily.split.caption', { orders: fmt.number(split.orderCount) })}
        {query.data.truncated
          ? ` ${t('dashboard.daily.split.partial', {
              shown: fmt.number(query.data.orders.length),
              total: fmt.number(query.data.total),
            })}`
          : ''}
        {drifted ? ` ${t('dashboard.daily.split.drift', { kg: fmt.withUnit('mass_kg', gap) })}` : ''}
      </Typography>
    </Box>
  );
};

export default DailyMaterialSplit;
